// src/app/core/services/cafe-config.service.ts
import { Injectable, signal, computed } from '@angular/core';
import { CafeConfig } from '../models/auth.model';

const STORAGE_KEY = 'bf_cafe_config';

const DEFAULT_CONFIG: CafeConfig = {
  workflowMode: 'FULL_SERVICE',
  enableKds: true,
  enableTableManagement: true,
  autoCompleteOrders: false,
  enableShiftTracking: true,
  printKitchenTicket: false,
};

@Injectable({ providedIn: 'root' })
export class CafeConfigService {
  private _config = signal<CafeConfig>(this.loadFromStorage());

  // Public readonly signals consumed by components
  readonly config         = this._config.asReadonly();
  readonly isQuickService = computed(() => this._config().workflowMode === 'QUICK_SERVICE');
  readonly showKds        = computed(() => this._config().enableKds);
  readonly showTables     = computed(() => this._config().enableTableManagement);
  readonly autoComplete   = computed(() => this._config().autoCompleteOrders);
  readonly showShiftTracking = computed(() => this._config().enableShiftTracking);
  readonly printKitchenTicket = computed(() => this._config().printKitchenTicket);

  /** Called after registration / login to persist the cafe's config */
  setConfig(config: Partial<CafeConfig>): void {
    const merged: CafeConfig = { ...DEFAULT_CONFIG, ...config };
    this._config.set(merged);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  }

  /** Update specific properties of the current config */
  updateConfig(config: Partial<CafeConfig>): void {
    const merged: CafeConfig = { ...this._config(), ...config };
    this._config.set(merged);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  }

  /** Build a config from the registration form workflow answers */
  buildFromWorkflowAnswers(answers: {
    businessProfile: string;
    orderTracking:   string;
    seatingType:     string;
  }): CafeConfig {
    const isQuick = answers.businessProfile === 'COFFEE_SHOP' ||
                    answers.businessProfile === 'FOOD_TRUCK'  ||
                    answers.businessProfile === 'BAKERY';

    const enableKds   = answers.orderTracking  === 'KDS';
    const enableTables = answers.seatingType !== 'TAKEAWAY_ONLY';
    const autoComplete = isQuick && !enableKds;
    const printKitchenTicket = answers.orderTracking === 'PRINT';

    return {
      workflowMode:          isQuick ? 'QUICK_SERVICE' : 'FULL_SERVICE',
      enableKds,
      enableTableManagement: enableTables,
      autoCompleteOrders:    autoComplete,
      enableShiftTracking:   true,
      printKitchenTicket,
    };
  }

  clear(): void {
    this._config.set(DEFAULT_CONFIG);
    localStorage.removeItem(STORAGE_KEY);
  }

  private loadFromStorage(): CafeConfig {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? { ...DEFAULT_CONFIG, ...JSON.parse(raw) } : DEFAULT_CONFIG;
    } catch {
      return DEFAULT_CONFIG;
    }
  }
}
